<template>
  <div class="six-dimension-analysis">
    <h3 class="section-title">
      <span>📊</span>
      <span>六维度职业兴趣分析</span>
    </h3>
    
    <div class="dimensions-grid">
      <div
        v-for="(type, index) in sortedTypes"
        :key="type.code"
        class="dimension-card"
        :class="{ 'top-rank': index === 0 }"
        :style="{ borderColor: type.color }"
      >
        <!-- 排名标签 -->
        <div class="rank-badge" :style="{ background: type.color }">
          <span v-if="index === 0">🏆</span>
          <span v-else>{{ index + 1 }}</span>
        </div>
        
        <!-- 类型图标和名称 -->
        <div class="type-header">
          <span class="type-emoji">{{ type.emoji }}</span>
          <h4 class="type-name" :style="{ color: type.color }">
            {{ type.fullName }}
          </h4>
        </div>
        
        <!-- 类型描述 -->
        <p class="type-description">{{ type.description }}</p>
        
        <!-- 分数进度条 -->
        <div class="score-bar-container">
          <div class="score-bar-bg">
            <div
              class="score-bar-fill"
              :style="{
                width: type.percentage + '%',
                background: type.gradient
              }"
            >
              <span class="score-percentage">{{ type.percentage }}%</span>
            </div>
          </div>
          <div class="score-detail">
            <span class="score-value">{{ type.score }}/15</span>
            <span class="score-level" :style="{ color: type.color }">{{ type.level }}</span>
          </div>
        </div>
        
        <!-- 性格特质标签 -->
        <div class="traits-container">
          <span
            v-for="trait in type.traits"
            :key="trait"
            class="trait-tag"
            :style="{
              borderColor: type.color,
              color: type.color,
              background: type.bgColor
            }"
          >
            {{ trait }}
          </span>
        </div>
        
        <!-- 适合职业 -->
        <div class="suitable-careers">
          <p class="suitable-label">适合职业：</p>
          <p class="suitable-text">{{ type.suitable }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { HOLLAND_TYPES } from '@/data/hollandTypes'

export default {
  name: 'SixDimensionAnalysis',
  props: {
    dimensionScores: {
      type: Object,
      required: true
    },
    rawScores: {
      type: Object,
      required: true
    }
  },
  computed: {
    sortedTypes() {
      return Object.entries(this.rawScores)
        .map(([code, score]) => {
          const typeInfo = HOLLAND_TYPES[code]
          const scoreInfo = this.dimensionScores[code]
          return {
            code,
            name: typeInfo?.name || code,
            fullName: typeInfo?.fullName || code,
            emoji: typeInfo?.emoji || '',
            color: typeInfo?.color || '#gray',
            bgColor: typeInfo?.bgColor || '#f3f4f6',
            gradient: typeInfo?.gradient || '#gray',
            description: typeInfo?.description || '',
            traits: typeInfo?.traits || [],
            suitable: typeInfo?.suitable || '',
            score,
            percentage: scoreInfo?.percentage || 0,
            level: scoreInfo?.level || '中等'
          }
        })
        .sort((a, b) => b.score - a.score)
    }
  }
}
</script>

<style scoped>
.six-dimension-analysis {
  margin: 2rem 0;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dimensions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.dimension-card {
  position: relative;
  background: white;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
  padding: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.dimension-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.dimension-card.top-rank {
  border-width: 3px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
}

/* 排名标签 */
.rank-badge {
  position: absolute;
  top: -12px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* 类型头部 */
.type-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.type-emoji {
  font-size: 2.5rem;
}

.type-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

/* 类型描述 */
.type-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
}

/* 分数进度条 */
.score-bar-container {
  margin-bottom: 1rem;
}

.score-bar-bg {
  position: relative;
  width: 100%;
  height: 32px;
  background: #f3f4f6;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.score-bar-fill {
  height: 100%;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 12px;
  transition: width 1s ease-out;
}

.score-percentage {
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
}

.score-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.score-value {
  color: #6b7280;
  font-weight: 600;
}

.score-level {
  font-weight: 700;
}

/* 性格特质标签 */
.traits-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.trait-tag {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  border: 1.5px solid;
  font-size: 0.75rem;
  font-weight: 600;
}

/* 适合职业 */
.suitable-careers {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.suitable-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 0.25rem;
}

.suitable-text {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .dimensions-grid {
    grid-template-columns: 1fr;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .dimension-card {
    padding: 1.25rem;
  }
}
</style>

